/* eslint-disable import/extensions */
import {type Inventory} from '@unbrs/plastic-data';
import * as Papa from 'papaparse';
import {getInventory} from '../../feeds';
import htmllistitem from './views/inventory-list-item.ktmpl.html';

export type BrsInventoryOptions = {
	inventory: Inventory.InventoryData | string;
	isFull?: boolean;
	resourceUrl?: string;
};

export class BrsInventory extends kendo.ui.Widget {
	constructor(selector: Element, options: BrsInventoryOptions) {
		super(selector, {isFull: false, ...options});

		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		this.onInit(this.options as BrsInventoryOptions);
	}

	async onInit(options: BrsInventoryOptions) {
		const template = kendo.template(htmllistitem);
		const inventory
			= typeof options.inventory === 'string'
				? await getInventory(options.inventory, options.resourceUrl ?? '')
				: options.inventory;
		if (inventory === undefined) {
			console.error('Inventory not found');
			return;
		}

		$(this.element).html(
			template({
				inventory: new InventoryView(inventory, {isFull: options.isFull}),
			}),
		);

		INVENTORY_PLOTS.init($('.brs-inventory-plots', this.element), inventory, options.isFull);

		$('button', this.element).on('click', event => {
			event.preventDefault();
			this.exportCsv(inventory);
		});
	}

	exportCsv(inventory: Inventory.InventoryData) {
		const view = new InventoryView(inventory, {isFull: true, isCsv: true});
		const rows = view.rowsFull();
		let data = rows.map(row => row.map(cell => cell.csv));
		data = [['Title', ...['Mega', 'Medium', 'Small', 'National'].map(title => `${title} (tones per year)`)], ...data];
		if (data.length === 0) {
			console.error('No data available to export.');
			return;
		}

		const csvContent = Papa.unparse(data, {
			delimiter: ';',
		});

		const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
		const link = document.createElement('a');
		const url = URL.createObjectURL(blob);

		link.setAttribute('href', url);
		link.setAttribute('download', `inventory ${view.title}.csv`);
		link.style.visibility = 'hidden';
		document.body.append(link);
		link.click();
		link.remove();
	}
}

declare global {
	// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
	interface JQuery {
		kendoBrsInventory(options: BrsInventoryOptions): JQuery;
		data(widgetName: 'kendoBrsInventory'): BrsInventory;
	}
}

type CellOptions = {
	isBold?: boolean;
	isIndent?: boolean;
	isPercentage?: boolean;
};

class Cell {
	private readonly _value?: string | number;
	private readonly _isBold: boolean;
	private readonly _isPercentage: boolean;
	private readonly _isIndent: boolean;

	constructor(value?: string | number, options: CellOptions = {}) {
		const {isBold = false, isIndent = false, isPercentage = false} = options;
		this._value = value;
		this._isBold = isBold;
		this._isPercentage = isPercentage;
		this._isIndent = isIndent;
	}

	get csv(): string | number {
		return this._value ?? '';
	}

	get html() {
		let html = this._value ? '' : '-';
		if (html === '-') {
			return html;
		}

		if (typeof this._value === 'number') {
			html += new Intl.NumberFormat('en-US', {
				useGrouping: true,
			}).format(this._value);
		} else {
			html += this._value;
		}

		if (this._isPercentage) {
			html += '%';
		}

		if (this._isBold) {
			html = '<b>' + html + '</b>';
		}

		if (this._isIndent) {
			html = '&nbsp;&nbsp' + html;
		}

		return html;
	}
}

type InventoryViewOptions = {
	isFull?: boolean;
	isCsv?: boolean;
};
class InventoryView {
	private readonly _inventory: Inventory.InventoryData;
	private readonly _options: InventoryViewOptions;

	constructor(
		public inventory: Inventory.InventoryData,
		options?: InventoryViewOptions,
	) {
		options = {isFull: false, isCsv: false, ...options};
		this._inventory = inventory;
		this._options = options ?? {};
	}

	get id() {
		return this._inventory.iso2 + '-' + this._inventory.year;
	}

	get title() {
		return `${this._inventory.country.name} — ${this._inventory.year}`;
	}

	empty(): Cell[] {
		return [
			new Cell(this._options.isCsv ? '' : '&nbsp;'),
			new Cell(''),
			new Cell(''),
			new Cell(''),
			new Cell(''),
		];
	}

	row(
		title: string,
		field: Exclude<keyof Inventory.InventoryData, 'year' | 'iso2' | 'country'>,
		options?: CellOptions,
	): Cell[] {
		const {
			isBold = false,
			isIndent = false,
			isPercentage = false,
		} = options ?? {};
		const cells: Cell[] = [];
		cells.push(new Cell(title, {isBold, isIndent}));

		if (this._options.isFull) {
			cells.push(
				new Cell(this._inventory[field]?.mega, {isPercentage}),
				new Cell(this._inventory[field]?.medium, {isPercentage}),
				new Cell(this._inventory[field]?.small, {isPercentage}),
			);
		}

		cells.push(new Cell(this._inventory[field]?.national, {isPercentage}));

		return cells;
	}

	isFull() {
		return this._options.isFull;
	}

	rowsShort(): Cell[][] {
		const rows: Cell[][] = [];
		rows.push(
			this.row('Total Plastic Waste Generation', 'total_generation'),
			this.row('Total collected', 'collected_total'),
			this.row('Total plastic waste recycled', 'recycled_total'),
			this.row(
				'Total plastic waste leakage from the waste management system',
				'leakage_total',
			),
			this.row('Total uncollected', 'uncollected_total'),
			this.row('Total unmanaged', 'unmanaged_total'),
		);
		return rows;
	}

	rowsFull(): Cell[][] {
		let rows: Cell[][] = [];
		rows.push(
			this.row('Total Plastic Waste Generation', 'total_generation', {
				isBold: true,
			}),
			this.empty(),
			this.row('Total collected', 'collected_total', {isBold: true}),
			this.row(
				'Tonnes plastic waste collected by formal mixed waste collection',
				'collected_formal_mixed',
				{isIndent: true},
			),
			this.row(
				'Tonnes plastic waste collected by informal mixed waste collection',
				'collected_informal_mixed',
				{isIndent: true},
			),
			this.row(
				'Tonnes plastic waste collected by formal recycling collections',
				'collected_formal_recycling',
				{isIndent: true},
			),
			this.row(
				'Tonnes plastic waste collected by informal recycling collections',
				'collected_informal_recycling',
				{isIndent: true},
			),
			this.row('Proportion of waste collected', 'collected_proportion', {
				isBold: true,
				isPercentage: true,
			}),
			this.empty(),
			this.row('Total plastic waste recycled', 'recycled_total', {
				isBold: true,
			}),
			this.row('Proportion recycled', 'recycled_proportion', {
				isPercentage: true,
			}),
			this.empty(),
			this.row(
				'Total plastic waste disposed to designated disposal sites',
				'disposed_total',
				{isBold: true},
			),
			this.row(
				'Plastic waste disposed in controlled landfills',
				'disposed_controlled',
				{isIndent: true},
			),
			this.row(
				'Plastic waste disposed in uncontrolled landfills',
				'disposed_uncontrolled',
				{isIndent: true},
			),
			this.row(
				'Recyclables taken from landfills by informal recyclers',
				'disposed_taken',
				{isBold: true},
			),
			this.row(
				'Net disposed (after waste plastics removed by informal recyclers)',
				'disposed_net',
				{isBold: true},
			),
			this.empty(),
			this.row(
				'Total plastic waste leakage from the waste management system',
				'leakage_total',
				{isBold: true},
			),
			this.row('Leakage during collections', 'leakage_collections', {
				isIndent: true,
			}),
			this.row('Leakage during transportation', 'leakage_transportation', {
				isIndent: true,
			}),
			this.row('Leakage during sorting', 'leakage_sorting', {isIndent: true}),
			this.row('Leakage from designated disposal sites', 'leakage_sites', {
				isIndent: true,
			}),
			this.row(
				'Proportion of all plastic waste leaking from the waste management system',
				'leakage_proportion',
				{isPercentage: true},
			),
			this.empty(),
			this.row('Total uncollected', 'uncollected_total', {isBold: true}),
			this.row(
				'Proportion of plastic waste uncollected',
				'uncollected_proportion',
				{isPercentage: true},
			),
			this.empty(),
			this.row('Total unmanaged', 'unmanaged_total', {isBold: true}),
			this.row(
				'Proportion of plastic waste mismanaged',
				'unmanaged_proportion',
				{isPercentage: true},
			),
			this.empty(),
			this.row('Total Plastic waste traded', 'traded_total', {isBold: true}),
			this.row('Total imports', 'imports_total'),
			this.row('Total exports', 'exports_total'),
		);
		if (this._options.isCsv) {
			rows = rows.filter(row => row[0] !== undefined && row[0].csv !== '');
		}

		return rows;
	}
}

type Field = keyof Inventory.InventoryData;
type PlotOptions = {
	stack?: boolean;
	reverse?: boolean;
};
const INVENTORY_PLOTS = {
	init(selector: JQuery, inventory: Inventory.InventoryData, isFull = false) {
		const div = '<div class="brs-inventory-plot"></div>';
		{
			const options = this.getSankeyChartOptions(inventory);
			$(div).appendTo(selector).kendoSankey(options);
		}

		if (!isFull) {
			return;
		}

		{
			const fields: Field[] = [
				'uncollected_total',
				'leakage_total',
				'disposed_net',
				'recycled_total',
			];
			const options = this.getBarChartOptions(inventory, 'Totals', fields);
			$(div).appendTo(selector).kendoChart(options);
			// $(div).appendTo('#brs-test').kendoChart(options);
		}

		{
			const fields: Field[] = [
				'leakage_collections',
				'leakage_transportation',
				'leakage_sorting',
				'leakage_sites',
			];
			const options = this.getBarChartOptions(
				inventory,
				'Tonnes of plastic leaked into the environment (tonnes per year)',
				fields,
				{stack: true},
			);
			$(div).appendTo(selector).kendoChart(options);
		}

		{
			const fields: Field[] = ['collected_total', 'uncollected_total'];
			const options = this.getBarChartOptions(
				inventory,
				'Plastic waste collected and uncollected (tonnes per year)',
				fields,
				{reverse: true},
			);
			$(div).appendTo(selector).kendoChart(options);
		}

		{
			const fields: Field[] = [
				'recycled_total',
				'disposed_total',
				'leakage_total',
			];
			const options = this.getPieChartOptions(
				inventory,
				'Totals (National)',
				fields,
				'national',
			);
			$(div).appendTo(selector).kendoChart(options).data('kendoChart');
		}
	},
	getInventoryLevelKeys(): Array<keyof Inventory.InventoryLevel> {
		return ['national', 'mega', 'medium', 'small'];
	},
	getFieldTitle(field: keyof Inventory.InventoryData): string {
		switch (field) {
			case 'collected_total': {
				return 'Total collected';
			}

			case 'uncollected_total': {
				return 'Total uncollected';
			}

			case 'leakage_total': {
				return 'Total plastic waste leakage from the waste management system';
			}

			case 'recycled_total': {
				return 'Total plastic waste recycled';
			}

			case 'disposed_total': {
				return 'Total plastic waste disposed to designated disposal sites';
			}

			case 'disposed_net': {
				return 'Net disposed (after waste plastics removed by informal recyclers)';
			}

			case 'leakage_collections': {
				return 'Leakage during collections';
			}

			case 'leakage_transportation': {
				return 'Leakage during transportation';
			}

			case 'leakage_sorting': {
				return 'Leakage during sorting';
			}

			case 'leakage_sites': {
				return 'Leakage from designated disposal sites';
			}

			default: {
				return `no title for field ${field}`;
			}
		}
	},
	getFieldTitleAndColor(field: keyof Inventory.InventoryData): {title: string; color: string} {
		switch (field) {
			case 'total_generation': {
				return {title: 'Total genrated', color: 'gray'};
			}

			case 'unmanaged_total': {
				return {title: 'Total unmanaged', color: 'black'};
			}

			case 'collected_total': {
				return {title: 'Total collected', color: '#30AB16'};
			}

			case 'uncollected_total': {
				return {title: 'Total uncollected', color: '#AB164E'};
			}

			case 'leakage_total': {
				return {title: 'Total plastic waste leakage from the waste management system', color: '#1716AB'};
			}

			case 'recycled_total': {
				return {title: 'Total plastic waste recycled', color: '#8416AB'};
			}

			case 'disposed_total': {
				return {title: 'Total plastic waste disposed to designated disposal sites', color: '#16AB9F'};
			}

			case 'disposed_net': {
				return {title: 'Net disposed (after waste plastics removed by informal recyclers)', color: '#3465A3'};
			}

			case 'leakage_collections': {
				return {title: 'Leakage during collections', color: '#5434A3'};
			}

			case 'leakage_transportation': {
				return {title: 'Leakage during transportation', color: '#A33497'};
			}

			case 'leakage_sorting': {
				return {title: 'Leakage during sorting', color: '#A3343B'};
			}

			case 'leakage_sites': {
				return {title: 'Leakage from designated disposal sites', color: '#A39734'};
			}

			default: {
				return {title: `no title for field ${field}`, color: '#000000'};
			}
		}
	},
	getBarChartOptions(
		inventory: Inventory.InventoryData,
		title: string,
		fields: Field[],
		options?: PlotOptions,
	): kendo.dataviz.ui.ChartOptions {
		const data: kendo.dataviz.ui.ChartSeriesItem[] = [];
		let categories: string[] = [];
		let colors: string[] = [];
		const defaultOptions = {
			stack: false,
			reverse: false,
		};
		options = Object.assign(defaultOptions, options);

		if (options.reverse) {
			for (const field of fields) {
				const row: any[] = [];
				for (const level of this.getInventoryLevelKeys()) {
					const item = inventory[field] as Inventory.InventoryLevel;
					row.push(item[level]);
				}

				data.push({
					name: this.getFieldTitle(field),
					data: row,
				});
			}

			colors = fields.map(field => this.getFieldTitleAndColor(field).color);
			categories = this.getInventoryLevelKeys();
		} else {
			for (const level of this.getInventoryLevelKeys()) {
				const row: any[] = [];
				for (const field of fields) {
					const item = inventory[field] as Inventory.InventoryLevel;
					row.push(item[level]);
				}

				data.push({
					name: level,
					data: row,
				});
			}

			categories = fields.map(field => this.getFieldTitleAndColor(field).title);
		}

		const chartOptions: kendo.dataviz.ui.ChartOptions = {
			title: {
				text: title,
			},
			legend: {
				visible: true,
				position: 'top',
			},
			seriesDefaults: {
				type: 'bar',
				stack: options.stack,
			},
			series: data,
			categoryAxis: {
				categories,
				majorGridLines: {
					visible: true,
				},
				labels: {
					template(label: {text: string}) {
						// Split the label into two lines if it exceeds a certain length
						const text = label.text;
						const maxLength = 40; // Adjust this value as needed
						if (text.length > maxLength) {
							const words = text.split(' ');
							const middle = Math.floor(words.length / 2);
							return (
								words.slice(0, middle).join(' ')
								+ '\n'
								+ words.slice(middle).join(' ')
							);
						}

						return text;
					},
				},
			},
			tooltip: {
				visible: true,
				shared: true,
				template: '#= series.name #: #= value #',
			},
			chartArea: {
				width: 760,
			},
		};

		if (colors.length > 0) {
			chartOptions.seriesColors = colors;
		}

		return chartOptions;
	},
	getPieChartOptions(
		inventory: Inventory.InventoryData,
		title: string,
		fields: Field[],
		level: keyof Inventory.InventoryLevel,
	): kendo.dataviz.ui.ChartOptions {
		const data: any[] = [];
		for (const field of fields) {
			const item = inventory[field] as Inventory.InventoryLevel;
			data.push({
				category: this.getFieldTitleAndColor(field).title,
				value: item[level],
			});
		}

		const colors = fields.map(field => this.getFieldTitleAndColor(field).color);
		const chartOptions: kendo.dataviz.ui.ChartOptions = {
			title: {
				text: title,
			},
			seriesColors: colors,
			chartArea: {
				background: '',
				width: 760,
			},
			legend: {
				visible: true,
				position: 'top',
			},
			seriesDefaults: {
				labels: {
					visible: true,
					template(label: {category: string; value: number}) {
						// Split the label into two lines if it exceeds a certain length
						let text = label.category;
						const maxLength = 40; // Adjust this value as needed
						if (text.length > maxLength) {
							const words = text.split(' ');
							const middle = Math.floor(words.length / 2);
							text
								= words.slice(0, middle).join(' ')
								+ '\n'
								+ words.slice(middle).join(' ');
						}

						return text + ': ' + label.value;
					},
				},
				tooltip: {
					visible: true,
					template: '#= category #: #= value #',
				},
			},
			series: [
				{
					type: 'pie',
					data,
				},
			],
		};
		return chartOptions;
	},

	getSankeyTitle(title: string, value: number | undefined): string {
		if (value === undefined) {
			return title;
		}

		return `${title} (${new Intl.NumberFormat('en-US', {useGrouping: true}).format(value)})`;
	},
	getSankeyChartOptions(inventory: Inventory.InventoryData): kendo.dataviz.ui.SankeyOptions {
		const nodes: kendo.dataviz.ui.SankeyNode[] = [
			{
				id: 'generatated_total',
				color: this.getFieldTitleAndColor('total_generation').color,
				label: {
					text: this.getSankeyTitle('Plastic waste generated', inventory.total_generation.national),
				},
			},
			{
				id: 'collected_total',
				color: this.getFieldTitleAndColor('collected_total').color,
				label: {
					text: this.getSankeyTitle('Collected', inventory.collected_total.national),
				},
			},
			{
				id: 'uncollected_total',
				color: this.getFieldTitleAndColor('uncollected_total').color,
				label: {
					text: this.getSankeyTitle('Uncollected', inventory.uncollected_total.national),
				},
			},
			{
				id: 'unmanaged_total',
				color: this.getFieldTitleAndColor('unmanaged_total').color,
				label: {
					text: this.getSankeyTitle('Unmanaged', inventory.unmanaged_total.national),
				},
			},
			{
				id: 'managed_total',
				color: '#30FB16',
				label: {
					text: this.getSankeyTitle('Managed',
						(inventory.recycled_total.national ?? 0) + (inventory.disposed_net.national ?? 0) + (inventory.exports_total?.national ?? 0)),
				},
			},
			{
				id: 'leakage_total',
				color: this.getFieldTitleAndColor('leakage_total').color,
				label: {
					text: this.getSankeyTitle('Leaked to the environment', inventory.leakage_total.national),
				},
			},
			{
				id: 'recyled_total',
				color: this.getFieldTitleAndColor('recycled_total').color,
				label: {
					text: this.getSankeyTitle('Recycled', inventory.recycled_total.national),
				},
			},
			{
				id: 'disposed_net',
				color: this.getFieldTitleAndColor('disposed_net').color,
				label: {
					text: this.getSankeyTitle('Disposed', inventory.disposed_net.national),
				},
			},
		];

		const links: kendo.dataviz.ui.SankeyLink[] = [
			{
				sourceId: 'generatated_total',
				targetId: 'collected_total',
				value: inventory.collected_total.national,
			},
			{
				sourceId: 'generatated_total',
				targetId: 'uncollected_total',
				value: inventory.uncollected_total.national,
			},
			{
				sourceId: 'uncollected_total',
				targetId: 'unmanaged_total',
				value: inventory.uncollected_total.national,
			},
			// {
			// 	sourceId: 'collected_total',
			// 	targetId: 'managed_total',
			// 	value: inventory.collected_total.national ?? 0 + (inventory.exports_total?.national as number ?? 0) - (inventory.leakage_total.national ?? 0),
			// },
			{
				sourceId: 'collected_total',
				targetId: 'leakage_total',
				value: inventory.leakage_total.national,
			},
			{
				sourceId: 'leakage_total',
				targetId: 'unmanaged_total',
				value: inventory.leakage_total.national,
			},
			{
				sourceId: 'collected_total',
				targetId: 'recyled_total',
				value: inventory.recycled_total.national,
			},
			{
				sourceId: 'collected_total',
				targetId: 'disposed_net',
				value: inventory.disposed_net.national,
			},
			{
				sourceId: 'recyled_total',
				targetId: 'managed_total',
				value: inventory.recycled_total.national,
			},
			{
				sourceId: 'disposed_net',
				targetId: 'managed_total',
				value: inventory.disposed_net.national,
			},
		];

		if (inventory.imports_total?.national) {
			nodes.push({
				id: 'imports_total',
				label: {
					text: this.getSankeyTitle('Imports', inventory.imports_total?.national),
				},
			},
			{
				id: 'exports_total',
				label: {
					text: this.getSankeyTitle('Exports', inventory.exports_total?.national),
				},
			});

			links.push({
				sourceId: 'imports_total',
				targetId: 'collected_total',
				value: inventory.imports_total?.national,
			});
		}

		if (inventory.exports_total?.national) {
			links.push({
				sourceId: 'collected_total',
				targetId: 'exports_total',
				value: inventory.exports_total?.national,
			}, {
				sourceId: 'exports_total',
				targetId: 'managed_total',
				value: inventory.exports_total?.national,
			});
		}

		const chartOptions: kendo.dataviz.ui.SankeyOptions = {
			title: {
				text: 'Plastic waste flows (tonnes per year)',
			},
			theme: 'sass',
			links: {colorType: 'source'},
			messages: {
				tooltipUnits: '({0} tonnes per year)',
			},
			legend: {
				margin: {
					top: 20,
				},
			},
			data: {
				nodes,
				links,
			},
		};
		return chartOptions;
	},
};
